import { forwardRef, useEffect, useImperativeHandle, useRef, useState } from "react";
import { useForm } from "react-hook-form";
import { useGetBitrixSessionIdQuery } from "src/api/rtk/indorsoftRu/slices/bitrixSessionApiSlice";
import { useCreateSupportTicketMutation, useCheckSupportModuleStatusQuery } from "src/api/rtk/indorsoftRu/slices/supportApiSlice";
import { getIndorImageSrc, IndorHuges } from "src/features";
import FileDropzoneWithPreview from "../fileDropzoneWithPreview/FileDropzoneWithPreview";
import css from "../supportModalShared.module.scss";
import { useDispatch } from "react-redux";
import { showToastMessage } from "src/stateManagement/reducers";
import { getItemWebStorage } from "src/features/webStorageSaver";

type ReportAnErrorModalProps = {
    open: boolean;
    onClose: () => void;
};

type ReportAnErrorFormValues = {
    title: string;
    message: string;
    criticality: 'medium' | 'high';
};

type ReportFormHandle = { hasUnsavedChanges: () => boolean }

export default function ReportAnErrorModal({ open, onClose }: ReportAnErrorModalProps) {
    const modalRef = useRef<HTMLDivElement>(null);
    const formRef = useRef<ReportFormHandle>(null);

    function attemptClose() {
        const hasChanges = formRef.current?.hasUnsavedChanges?.() ?? false;
        if (hasChanges) {
            const confirmed = window.confirm("Вы уверены, что хотите закрыть окно? Все введённые данные будут удалены.");
            if (!confirmed) return;
        }
        onClose();
    }

    useEffect(() => {
        if (!open) return;
        const onKeyDown = (e: KeyboardEvent) => {
            if (e.key === "Escape") attemptClose();
        };
        document.addEventListener("keydown", onKeyDown);
        return () => document.removeEventListener("keydown", onKeyDown);
    }, [open]);

    function onOverlayClick(e: any) {
        if (e.target === modalRef.current) attemptClose();
    }

    if (!open) return null as any;

    return (
        <div ref={modalRef} className={css.overlay} onMouseDown={onOverlayClick}>
            <div className={css.modal} role="dialog" aria-modal="true" aria-labelledby="report-error-title">
                <div className={css.header}>
                    <div className={css.brand}>
                        <img src={getIndorImageSrc(IndorHuges.HelpSendBugs, 128, "IndorHuges")} className={css.titleIcon} />
                        <span id="report-error-title" className={css.title}>Сообщение об ошибке</span>
                    </div>
                    <button className={css.closeBtn} aria-label="Закрыть" onClick={attemptClose}>×</button>
                </div>

                <ReportForm ref={formRef} onClose={onClose} />
            </div>
        </div>
    );
}

const ReportForm = forwardRef<ReportFormHandle, { onClose: () => void }>(function ReportForm({ onClose }, ref) {
    const dispatch = useDispatch();
    const currentConfig = getItemWebStorage("currentConfig") ?? {};
    const isSupportEnabledInConfig = currentConfig?.support?.enabled ?? false;

    const { data: bitrixSessionData } = useGetBitrixSessionIdQuery(undefined, {
        skip: !isSupportEnabledInConfig,
    });
    const { data: supportModuleStatus, isLoading: isCheckingModuleStatus } = useCheckSupportModuleStatusQuery(undefined, {
        skip: !isSupportEnabledInConfig,
        refetchOnMountOrArgChange: true,
    });
    const [createTicket, { isLoading, isSuccess, error }] = useCreateSupportTicketMutation()

    const { register, handleSubmit, formState: { errors }, reset, watch }: any = useForm<ReportAnErrorFormValues>({
        defaultValues: { title: "", message: "", criticality: "medium" }
    });

    const [files, setFiles] = useState<File[]>([]);
    const [serverErrorText, setServerErrorText] = useState<string | null>(null);
    const [filesErrorText, setFilesErrorText] = useState<string | null>(null);

    const MAX_FILES = 10;
    const MAX_TOTAL_SIZE_BYTES = 15000 * 1024; // 15 000 Кб ≈ 14.65 Мб
    const totalBytes = files.reduce((acc, f) => acc + (f?.size ?? 0), 0);
    const percent = Math.min(100, Math.round((totalBytes / MAX_TOTAL_SIZE_BYTES) * 100));
    const exceedLimit = totalBytes > MAX_TOTAL_SIZE_BYTES;

    function formatMb(bytes: number) {
        return (bytes / (1024 * 1024)).toFixed(2);
    }

    function onAddFiles(fileList: FileList | File[]) {
        if (!fileList) return;
        const next = Array.isArray(fileList) ? fileList : Array.from(fileList);
        const newTotal = files.length + next.length;
        const merged = [...files, ...next];
        const limited = merged.slice(0, MAX_FILES);
        setFiles(limited);
        setFilesErrorText(newTotal > MAX_FILES ? `Можно прикрепить не более ${MAX_FILES} файлов` : null);
    }

    function removeFile(idx: number) {
        setFiles(prev => prev.filter((_, i) => i !== idx));
        setFilesErrorText(null);
    }

    useImperativeHandle(ref, () => ({
        hasUnsavedChanges: () => {
            const values = watch();
            return !!(values?.title?.trim() || values?.message?.trim() || files.length);
        }
    }));

    function onReset() {
        const values = watch();
        const hasValues = !!(values?.title?.trim() || values?.message?.trim() || values?.criticality || files.length);
        if (hasValues) {
            const confirmed = window.confirm("Вы уверены, что хотите очистить форму? Все введённые данные будут удалены.");
            if (!confirmed) return;
        }
        reset();
        setFiles([]);
        setServerErrorText(null);
        setFilesErrorText(null);
    }

    async function onSubmit(values: ReportAnErrorFormValues) {
        const sessid = bitrixSessionData?.sessid
        if (sessid) {
            createTicket({ sessid, values, files })
        }
    }

    useEffect(() => {
        if (isSuccess) {
            onClose();
            dispatch(showToastMessage({ messageText: "Сообщение об ошибке успешно отправлено", messageType: "success" }))
        }
    }, [isSuccess])

    useEffect(() => {
        if (error) {
            setServerErrorText('Не удалось отправить обращение. Попробуйте позже.');
            console.error(error);
        }
    }, [error])


    // Если модуль не активен, показываем сообщение
    if (!isSupportEnabledInConfig) {
        return (
            <div className={css.form}>
                <div className={css.centeredMessage}>
                    Модуль тех. поддержки отключён в конфигурации базы.
                </div>
            </div>
        );
    }

    if (isCheckingModuleStatus) {
        return (
            <div className={css.form}>
                <div className={css.centeredMessage}>
                    Проверка доступности службы поддержки...
                </div>
            </div>
        );
    }

    if (!supportModuleStatus?.isActive) {
        return (
            <div className={css.form}>
                <div style={{ textAlign: 'center', padding: '40px 20px' }}>
                    <p style={{ fontSize: '16px', color: '#dc2626', marginBottom: '10px', fontWeight: 500 }}>
                        Служба тех. поддержки временно недоступна
                    </p>
                    <p style={{ fontSize: '14px', color: '#6b7280' }}>
                        Обратитесь к администратору системы.
                    </p>
                </div>
            </div>
        );
    }

    return (
        <form className={css.form} onSubmit={handleSubmit(onSubmit)}>
            <label className={css.label} htmlFor="ticket-title">Заголовок:</label>
            <input
                id="ticket-title"
                type="text"
                placeholder="Краткое наименование ошибки"
                className={css.input}
                aria-invalid={!!errors["title"]}
                {...register("title", { required: "Введите заголовок" })}
            />
            {errors["title"] && <span className={css.error}>{errors["title"].message as string}</span>}

            <label className={css.label} htmlFor="ticket-message">Сообщение:</label>
            <textarea
                id="ticket-message"
                placeholder="Опишите выявленную ошибку"
                className={css.textarea}
                rows={7}
                aria-invalid={!!errors["message"]}
                {...register("message", { required: "Введите текст сообщения" })}
            />
            {errors["message"] && <span className={css.error}>{errors["message"].message as string}</span>}

            <div className={css.filesBlock}>
                <div className={css.filesHeader}>
                    <span className={css.label}>Прикрепить файлы:</span>
                    <div className={css.progress}>
                        <div className={css.progressTrack}>
                            <div
                                className={`${css.progressFill} ${exceedLimit ? css.over : ''}`}
                                style={{ width: `${percent}%` }}
                            />
                        </div>
                        <span className={css.progressText}>
                            {formatMb(totalBytes)} Мб из 14.65 Мб ({percent}%)
                        </span>
                    </div>
                </div>
                {exceedLimit && (
                    <span className={css.error}>Превышен общий лимит прикреплённых файлов</span>
                )}
                {filesErrorText && (
                    <span className={css.error}>{filesErrorText}</span>
                )}
                <FileDropzoneWithPreview
                    files={files}
                    onAddFiles={onAddFiles}
                    onRemoveFile={removeFile}
                    disabled={files.length >= MAX_FILES}
                />
            </div>

            <label className={css.label} htmlFor="ticket-criticality">
                <span className={css.warningIcon} aria-hidden>⚠️</span>
                Критичность:
            </label>
            <select id="ticket-criticality" className={css.select} {...register("criticality")}>
                <option value="medium">Средняя (ошибка)</option>
                <option value="high">Высокая (критичная ошибка)</option>
            </select>

            {serverErrorText && <span className={css.error}>{serverErrorText}</span>}

            <div className={css.actions}>
                <button type="submit" className={css.submitBtn} disabled={isLoading || exceedLimit}>
                    {isLoading ? "Отправка..." : "Отправить"}
                </button>
                <button type="button" className={css.resetBtn} onClick={onReset}>Сбросить</button>
            </div>
        </form>
    );
});


